<template>
    <div class="ppv2-w-full ppv2-px-8 ppv2-py-8">
        <!-- Back link -->
        <div class="ppv2-mb-6">
            <a
                :href="backUrl"
                class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-text-gray-500 dark:ppv2-text-gray-300 hover:ppv2-text-[#7D2DBD] ppv2-transition-colors"
            >
                <ArrowLeft :size="20" />
                <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ $t('common.return') }}
                </span>
            </a>
        </div>

        <!-- Title -->
        <h2 class="poppinsSemibold ppv2-text-black dark:ppv2-text-white ppv2-mb-8" :style="{ fontSize: normalizeFontSize(20) }">
            {{ $t('voucher.send_title') }}
        </h2>

        <!-- Form Card -->
        <div class="ppv2-bg-white dark:ppv2-bg-dark ppv2-rounded-2xl ppv2-shadow-sm ppv2-p-8">
            <!-- Section: Informations generales -->
            <h3 class="poppinsMedium ppv2-text-[#7D2DBD] ppv2-text-center ppv2-mb-6" :style="{ fontSize: normalizeFontSize(16) }">
                {{ $t('voucher.general_info') }}
            </h3>

            <!-- Radio: target -->
            <div class="ppv2-mb-8">
                <BaseRadioGroup v-model="form.send_target" :options="sendTargetOptions" />
            </div>

            <!-- Email: buyer -->
            <div v-if="form.send_target === 'buyer'" class="ppv2-mb-6">
                <BaseInput
                    v-model="form.from_email"
                    :label="$t('voucher.buyer_email')"
                    type="email"
                    :placeholder="$t('voucher.buyer_email_placeholder')"
                    :error="errors.from_email ? errors.from_email[0] : ''"
                />
            </div>

            <!-- Email: recipient -->
            <div v-if="form.send_target === 'recipient'" class="ppv2-mb-6">
                <BaseInput
                    v-model="form.to_email"
                    :label="$t('voucher.recipient_email')"
                    type="email"
                    :placeholder="$t('voucher.recipient_email_placeholder')"
                    :error="errors.to_email ? errors.to_email[0] : ''"
                />
            </div>

            <!-- Message -->
            <div class="ppv2-mb-8">
                <BaseTextarea
                    v-model="form.from_message"
                    :label="$t('voucher.message_optional')"
                    :placeholder="$t('voucher.message_placeholder')"
                    :rows="5"
                    :useEditor="false"
                />
            </div>

            <!-- Submit -->
            <div class="ppv2-flex ppv2-justify-center">
                <BaseButton variant="primary" :loading="loading" :disabled="loading" @click="submitForm">
                    {{ $t('voucher.resend') }}
                </BaseButton>
            </div>
        </div>
    </div>
</template>

<script>
import { ArrowLeft } from 'lucide-vue'
import BaseInput from '../../../Shared/BaseInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BaseRadioGroup from '../../../Shared/BaseRadioGroup.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import axios from 'axios'

export default {
    name: 'SendVoucherForm',
    components: {
        ArrowLeft,
        BaseInput,
        BaseTextarea,
        BaseButton,
        BaseRadioGroup,
    },
    props: {
        voucherId: {
            type: [Number, String],
            required: true,
        },
        initialFromEmail: {
            type: String,
            default: '',
        },
        initialToEmail: {
            type: String,
            default: '',
        },
        initialMessage: {
            type: String,
            default: '',
        },
        backUrl: {
            type: String,
            default: '/back-office/bons_cadeau-v2',
        },
        csrfToken: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            loading: false,
            form: {
                send_target: 'recipient',
                from_email: this.initialFromEmail || '',
                to_email: this.initialToEmail || '',
                from_message: this.initialMessage || '',
            },
            errors: {},
        }
    },
    computed: {
        sendTargetOptions() {
            return [
                { value: 'buyer', label: this.$t('voucher.resend_to_buyer') },
                { value: 'recipient', label: this.$t('voucher.resend_to_recipient') },
            ]
        },
    },
    methods: {
        normalizeSize,
        normalizeFontSize,
        async submitForm() {
            this.loading = true
            this.errors = {}

            try {
                const formData = new FormData()
                formData.append('send_target', this.form.send_target)
                formData.append('from_email', this.form.from_email)
                formData.append('to_email', this.form.to_email)
                formData.append('from_message', this.form.from_message)
                formData.append('_token', this.csrfToken)

                await axios.post(`/back-office/bons_cadeau/${this.voucherId}/send_voucher`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })

                this.$toast?.success?.(this.$t('voucher.resendSuccess'))
            } catch (error) {
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors || {}
                    this.$toast?.error?.(this.$t('voucher.resendError'))
                } else if (error.response && error.response.data && error.response.data.message) {
                    this.$toast?.error?.(error.response.data.message)
                } else {
                    this.$toast?.error?.(this.$t('voucher.resendError'))
                }
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
input[type='radio'] {
    accent-color: #7d2dbd;
}

/* Dark mode adjustments */
:deep(.dark) .ppv2-text-black {
    color: #e6e6e6 !important;
}

:deep(.dark) .ppv2-bg-white {
    background-color: #0f1720 !important;
}

:deep(.dark) .ppv2-text-gray-500 {
    color: #9ca3af !important;
}
</style>
